/**
 * Copyright 2025 Lumavec Group LLC
 *
 * ThreeWayDiffViewer - Shows base, local, and remote versions side by side
 * with resolution actions and preview
 */

import React, { useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Edit3,
  Check,
  X,
  Loader,
  Copy,
  Eye,
  AlertCircle,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { ConflictResolution, AIResolutionResult, ResolutionMethod } from './types';

interface ThreeWayDiffViewerProps {
  conflict: ConflictResolution;
  onAcceptLocal: () => void;
  onAcceptRemote: () => void;
  onAcceptAI: () => void;
  onRequestAI: () => void;
  onSaveManual: (content: string) => void;
  onDefer: () => void;
  onClose: () => void;
  isResolvingAI: boolean;
}

export function ThreeWayDiffViewer({
  conflict,
  onAcceptLocal,
  onAcceptRemote,
  onAcceptAI,
  onRequestAI,
  onSaveManual,
  onDefer,
  onClose,
  isResolvingAI
}: ThreeWayDiffViewerProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [showAIDetails, setShowAIDetails] = useState(false);
  const [activeTab, setActiveTab] = useState<'side-by-side' | 'result'>('side-by-side');

  const { baseVersion, localVersion, remoteVersion } = conflict.conflict;
  const aiResult = conflict.aiResult;

  const handleStartEdit = () => {
    // Start with local version as base for editing
    setEditContent(localVersion);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    onSaveManual(editContent);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent('');
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-green-600 bg-green-100';
    if (confidence >= 0.7) return 'text-yellow-600 bg-yellow-100';
    return 'text-orange-600 bg-orange-100';
  };

  const renderVersionPanel = (
    title: string,
    content: string,
    color: string,
    onAccept?: () => void,
    acceptLabel?: string
  ) => (
    <div className={`flex-1 flex flex-col min-w-0 border ${color} rounded-lg overflow-hidden`}>
      <div className={`px-3 py-2 ${color.replace('border', 'bg').replace('-300', '-50')} border-b ${color} flex items-center justify-between`}>
        <span className="font-medium text-sm">{title}</span>
        <button
          onClick={() => copyToClipboard(content)}
          className="p-1 hover:bg-gray-200 rounded"
          title="Copy to clipboard"
        >
          <Copy size={14} className="text-gray-500" />
        </button>
      </div>
      <div className="flex-1 overflow-auto">
        <pre className="p-3 text-xs font-mono whitespace-pre-wrap break-words bg-white min-h-[200px]">
          {content || <span className="text-gray-400 italic">(empty)</span>}
        </pre>
      </div>
      {onAccept && (
        <div className="p-2 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onAccept}
            disabled={conflict.status === 'resolved'}
            className="w-full btn-secondary text-xs py-1.5 flex items-center justify-center gap-1"
          >
            <Check size={12} />
            {acceptLabel || 'Accept'}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">
            Resolve: {conflict.conflict.entityId}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {conflict.conflict.filePath}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {conflict.status === 'resolved' && (
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
              Resolved
            </span>
          )}
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 bg-gray-50">
        <button
          onClick={() => setActiveTab('side-by-side')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${
            activeTab === 'side-by-side'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Compare Versions
        </button>
        <button
          onClick={() => setActiveTab('result')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${
            activeTab === 'result'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {isEditing ? 'Edit Resolution' : 'Resolution Preview'}
        </button>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {activeTab === 'side-by-side' ? (
          <>
            {/* Three-way comparison */}
            <div className="flex-1 flex gap-2 p-3 overflow-hidden">
              {/* Base version */}
              {renderVersionPanel(
                'Base (Common Ancestor)',
                baseVersion,
                'border-gray-300'
              )}
              
              {/* Local version */}
              {renderVersionPanel(
                'Local (Your Changes)',
                localVersion,
                'border-blue-300',
                onAcceptLocal,
                'Accept Local'
              )}
              
              {/* Remote version */}
              {renderVersionPanel(
                'Remote (Their Changes)',
                remoteVersion,
                'border-purple-300',
                onAcceptRemote,
                'Accept Remote'
              )}
            </div>

            {/* AI Resolution section */}
            <div className="border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowAIDetails(!showAIDetails)}
                className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-100"
              >
                <div className="flex items-center gap-2">
                  {showAIDetails ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <Sparkles size={16} className="text-purple-600" />
                  <span className="font-medium text-sm">AI Resolution</span>
                  {aiResult && (
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getConfidenceColor(aiResult.confidence)}`}>
                      {Math.round(aiResult.confidence * 100)}% confidence
                    </span>
                  )}
                </div>
                {!aiResult && !isResolvingAI && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestAI();
                    }}
                    className="btn-primary text-xs py-1 px-3"
                  >
                    <Sparkles size={12} className="inline mr-1" />
                    Generate
                  </button>
                )}
                {isResolvingAI && (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <Loader size={14} className="animate-spin" />
                    Analyzing...
                  </div>
                )}
              </button>

              {showAIDetails && aiResult && (
                <div className="px-4 pb-4 border-t border-gray-200">
                  {/* Reasoning */}
                  <div className="mt-3 mb-3 p-3 bg-white rounded-lg border border-gray-200">
                    <h4 className="text-xs font-medium text-gray-700 mb-1">AI Reasoning</h4>
                    <p className="text-sm text-gray-600">{aiResult.reasoning}</p>
                  </div>

                  {/* Field-level changes */}
                  {aiResult.changes && aiResult.changes.length > 0 && (
                    <div className="mb-3">
                      <h4 className="text-xs font-medium text-gray-700 mb-2">Changes Made</h4>
                      <div className="space-y-1">
                        {aiResult.changes.map((change, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-xs">
                            <span className={`px-1.5 py-0.5 rounded ${
                              change.decision === 'kept_local' ? 'bg-blue-100 text-blue-700' :
                              change.decision === 'kept_remote' ? 'bg-purple-100 text-purple-700' :
                              change.decision === 'merged' ? 'bg-green-100 text-green-700' :
                              'bg-orange-100 text-orange-700'
                            }`}>
                              {change.field}
                            </span>
                            <span className="text-gray-600">{change.explanation}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Accept AI button */}
                  <button
                    onClick={onAcceptAI}
                    disabled={conflict.status === 'resolved'}
                    className="w-full btn-primary text-sm py-2 flex items-center justify-center gap-2"
                  >
                    <Check size={14} />
                    Accept AI Resolution
                  </button>
                </div>
              )}

              {showAIDetails && !aiResult && !isResolvingAI && (
                <div className="px-4 pb-4 text-center text-sm text-gray-500">
                  <p>Click "Generate" to get an AI-suggested resolution</p>
                </div>
              )}
            </div>
          </>
        ) : (
          /* Result/Edit tab */
          <div className="flex-1 flex flex-col overflow-hidden p-4">
            {isEditing ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Edit Resolution</span>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCancelEdit}
                      className="btn-secondary text-xs py-1 px-3"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className="btn-primary text-xs py-1 px-3"
                    >
                      <Check size={12} className="inline mr-1" />
                      Save
                    </button>
                  </div>
                </div>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="flex-1 w-full p-3 text-xs font-mono border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter the resolved content..."
                />
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    {conflict.status === 'resolved' ? 'Resolved Content' : 'Preview'}
                  </span>
                  {conflict.status !== 'resolved' && (
                    <button
                      onClick={handleStartEdit}
                      className="btn-secondary text-xs py-1 px-3 flex items-center gap-1"
                    >
                      <Edit3 size={12} />
                      Edit Manually
                    </button>
                  )}
                </div>
                <div className="flex-1 overflow-auto bg-white border border-gray-200 rounded-lg">
                  <pre className="p-3 text-xs font-mono whitespace-pre-wrap break-words">
                    {conflict.resolvedContent || (
                      aiResult?.resolution ? (
                        JSON.stringify(aiResult.resolution, null, 2)
                      ) : (
                        <span className="text-gray-400 italic">
                          No resolution selected yet. Choose a version or generate an AI resolution.
                        </span>
                      )
                    )}
                  </pre>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
        <button
          onClick={onDefer}
          disabled={conflict.status === 'resolved'}
          className="btn-secondary text-sm py-2 px-4"
        >
          Defer for Later
        </button>
        
        <div className="text-xs text-gray-500">
          {conflict.status === 'resolved' ? (
            <span className="flex items-center gap-1 text-green-600">
              <Check size={14} />
              Conflict resolved via {conflict.method}
            </span>
          ) : (
            <span>Choose a resolution method above</span>
          )}
        </div>
      </div>
    </div>
  );
}
